import { getRequiredApiBaseUrl } from 'config/apiBaseUrl';

import type {
  EndUserDashboardState,
  EndUserStateResponse,
} from 'services/api/endUserDashboardApi.data';
import {
  pickEndUserDisplayName,
  pickEndUserEmail,
  pickEndUserOrgName,
  pickEndUserUnitName,
} from 'services/api/endUserDashboardApi.helpers';
import { userApiFetchWithOptionalCognitoRefresh } from 'services/api/userApiFetchWithOptionalCognitoRefresh';
import { normalizeEndUserDashboardSteps } from 'services/endUser/endUserDashboardSteps.data';

import { parseApiErrorMessageFromResponseText } from '@/services/auth/apiErrorMessage';

const getApiBase = (): string => getRequiredApiBaseUrl();

const getErrorMessage = (response: Response, text: string): string =>
  parseApiErrorMessageFromResponseText(response, text, 'Failed to fetch state');

export const fetchEndUserDashboardState = async (
  euId: string,
  idToken: string
): Promise<EndUserDashboardState> => {
  const url = `${getApiBase()}/eu/${encodeURIComponent(euId)}/state`;
  const response = await userApiFetchWithOptionalCognitoRefresh(url, idToken, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  const rawText = await response.text();
  const parsed: EndUserStateResponse = rawText ? (JSON.parse(rawText) as EndUserStateResponse) : {};

  if (!response.ok || parsed.success === false) {
    throw new Error(getErrorMessage(response, rawText));
  }

  const payload = parsed.data ?? {};

  return {
    accountState: payload.accountState ?? null,
    expenseConfirmationConsentAccepted: payload.expenseConfirmationConsentAccepted === true,
    firstName: pickEndUserDisplayName(payload) ?? 'there',
    email: pickEndUserEmail(payload),
    hasExpenses: payload.hasExpenses === true,
    message: parsed.message ?? 'Account successfully connected',
    orgName: pickEndUserOrgName(payload),
    profileState: payload.state?.trim() || null,
    steps: normalizeEndUserDashboardSteps(payload.steps),
    unitName: pickEndUserUnitName(payload),
  };
};
